import _ from 'lodash'
import { EXPIRING_NOTE, PRODUCT_WARRANTY_SCOPE_LIST, PRODUCT_WARRANTY_STATUS, PRODUCT_WARRANTY_STATUS_MAP } from '@vzmi/shp_shared_compo/constants/proposal'
import {
  extractTransientProduct,
  setShipTypeFormSectionByFormData,
  setActiveTransientData,
  isNoneWarrantyPeriod,
  getShipTypeDefaultFormData,
  extractShipTypeFormSection,
  syncDataBySetting,
  syncDataByIdentity,
  omitFormSettingFields,
  formatUploadedVideoFile,
  withHTMLCheck,
  getAttrTransientKey,
  getSpecAttributeFieldPrefixByLevel
} from '../../Common/ProposalForm/Shared/util'
import {
  WARRANTY_CUSTOM_SCOPE_FIELD,
  WARRANTY_SCOPE_FIELD,
  WARRANTY_FIELD,
  DEFAULT_FORM_DATA,
  WARRANTY_STATUS_FIELD
} from '../../Shared'
import { WARRANTY_SCOPE_OTHER } from '../../../../constants/proposalTypes'
import { isEmptyStr } from '../../../../lib/Validator'
import { calculateDaysFromDateType } from '../../../../lib/dateUtil'
import {
  isNewListingProposal,
  isNotPhaseOverview,
  isNewProductProposal
} from '../../../../../lib/proposalHelper'
import { getDefaultTimezoneMoment } from '../../../../../lib/renderUtil'
import { getShipTypeIdFromAllValues } from '../../../../lib/ReduxFormSelectors/ProposalListingForm'

const SHORT_DESCRIPTION_LENGTH = 5

export const transformWarrantyScope = (warranty = {}) => {
  // 如果保固範圍選擇其他 將product.warranty.custom_scope 其他保固範圍的值塞回欄位
  if (WARRANTY_SCOPE_OTHER === warranty.scope) {
    const warrantyCustomScope = _.get(
      { product: { warranty } },
      WARRANTY_CUSTOM_SCOPE_FIELD
    )
    return {
      ...warranty,
      scope: warrantyCustomScope,
      custom_scope: null
    }
  }
  return warranty
}

// Converting reduxForm data to raw proposal data
export const transformFormDataToProposalData = formValues => {
  const clonedFormValues = _.cloneDeep(formValues)
  const {
    type,
    setting
  } = clonedFormValues

  // prepare `proposal.product` attributes
  // 1. taking out product and attributes from transient fields
  // 2. extract ship type form section
  // 3. reformat it to the submittable data
  const transientProduct = extractTransientProduct(clonedFormValues)
  const shipTypeFormSection = extractShipTypeFormSection(clonedFormValues)
  // omit useless values after values have been picked out and sorted
  let pureFormValues = omitFormSettingFields(clonedFormValues)
  const { product } = pureFormValues

  let warranty = _.get(product, 'warranty', {})

  const { productStatus } = warranty
  if (_.isUndefined(productStatus)) {
    _.set(warranty, 'productStatus', PRODUCT_WARRANTY_STATUS.NEW)
  }

  // 產品狀態跟預設允許的不同時強制全部轉換成 null 由表單驗證阻擋讓 user 重選
  if (!(productStatus in PRODUCT_WARRANTY_STATUS_MAP)) {
    _.set(warranty, 'productStatus', null)
  }

  // 如果內容值為`無保固` 則保固範圍及保固來源可不填，反之若為其他值則必填
  if (isNoneWarrantyPeriod(warranty.period)) {
    warranty.handler = null
    warranty.scope = null
    _.set(pureFormValues, 'product.warranty', warranty)
  }

  // 如果保固範圍選擇其他 將product.warranty.custom_scope 其他保固範圍的值塞回欄位
  warranty = transformWarrantyScope(warranty)
  _.set(pureFormValues, WARRANTY_FIELD, warranty)

  // 提案一般賣場且 reviewStatus 為 draft 以上時必填
  if (isNewListingProposal(type)) {
    _.set(
      pureFormValues,
      'listing.catItemId',
      _.get(pureFormValues, 'product.catItemId')
    )
  }

  // remove thumbnail from product.models[0].videos[0]
  const videoField = 'product.models[0].videos[0]'
  const modelVideo = _.get(pureFormValues, videoField, null)
  if (!_.isEmpty(modelVideo)) {
    delete modelVideo.thumbnail
    if (!modelVideo.url) {
      _.set(pureFormValues, videoField, {})
    }
  } else {
    _.set(pureFormValues, videoField, formatUploadedVideoFile(modelVideo))
  }

  // merge shipTypeForm 中的 attributes 進入主要 pureFormValues
  _.merge(pureFormValues, shipTypeFormSection, { product: transientProduct })

  const { product: shipTypeFormProduct } = pureFormValues
  const { years, months, days } = shipTypeFormProduct
  const isPreserveDaysExist = !!(years || months || days)
  if (isPreserveDaysExist) {
    // 如果有保存期限就需要將年月日換算回天數
    const totalPreserveDays = calculateDaysFromDateType(shipTypeFormProduct)
    const extraUnUsedPreserveDateTypesProduct = _.flow(
      product => _.omit(product, ['years', 'months', 'days']),
      newProduct => ({ ...newProduct, preserveDays: totalPreserveDays })
    )(shipTypeFormProduct)
    _.set(pureFormValues, 'product', extraUnUsedPreserveDateTypesProduct)
  } else {
    const resetPreserveDateProduct = {
      ...shipTypeFormProduct,
      preserveDays: null
    }
    _.set(pureFormValues, 'product', resetPreserveDateProduct)
  }

  // 依據身份來重設資料
  pureFormValues = syncDataByIdentity(setting, pureFormValues)

  // 依據SupplierProfile的設定來重設表單數值
  pureFormValues = syncDataBySetting(setting, pureFormValues)

  const isTaxFreeSubStation = (
    _.get(formValues, 'subStationId') === 'sub43'
  )
  const taxFree = _.get(pureFormValues, 'product.taxFree')
  // 免稅欄位轉換成應税
  // 非書籍子站 sub43 固定非免稅
  _.set(pureFormValues, 'product.taxable', isTaxFreeSubStation ? !taxFree : true)

  // the following list must be omitted before sending api
  pureFormValues = _.omit(pureFormValues, [
    'creator',
    'modifier',
    'executeStatus',
    'product.catId',
    'product.taxFree'
  ])
  pureFormValues.listing.discount = undefined

  // 防止先選了即期品再把保存天數從有改無的情況 拿保存天數(天數欄位)來判斷
  if (_.isNil(_.get(pureFormValues, 'product.preserveDays'))) {
    _.set(pureFormValues, 'product.isExpiringItem', false)
  }
  // 如果是即期品則將商品名稱加入即期品字樣
  const isExpiringItem = 'product.isExpiringItem'
  const productName = _.get(pureFormValues, 'product.name', '') || ''
  if (_.get(pureFormValues, isExpiringItem)) {
    // 判斷是否有填寫賣場名稱
    if (!isEmptyStr(productName)) {
      _.set(pureFormValues, 'product.name', EXPIRING_NOTE.STR + productName)
    } else {
      _.set(pureFormValues, 'product.name', EXPIRING_NOTE.STR)
    }

    // 進倉欄位(warehouseProductName)也需要加上即期品字樣
    const models = _.get(pureFormValues, 'product.models', [])
    _.forEach(models, model => {
      const items = _.get(model, 'items', [])
      _.forEach(items, item => {
        if (item.warehouseProductName) {
          item.warehouseProductName = EXPIRING_NOTE.STR + item.warehouseProductName
        }
      })
    })
  }

  // 新增商品提案需刪除特定欄位
  // https://thestreet.ouroath.com/docs/DOC-259849
  if (isNewProductProposal(type)) {
    pureFormValues = _.merge({}, pureFormValues, {
      product: {},
      listing: {
        seoUrl: null, // 無中文化網址
        featureTitle: null, // 無特色標題
        onShelvedTs: null, // 無開始時間
        offShelvedTs: null, // 無結束時間
        isDisplay: null, // 無賣場顯示設定
        deliveryType: null, // 無交貨期限
        price: null, // 無購物中心售價
        purchaseQtyLimit: null, // 無單次限購數量(宅配)
        cvsPurchaseQtyLimit: null // 無單次限購數量(超取)
      }
    })
  }

  return pureFormValues
}

// Parsing raw proposal data to reduxForm data
export const transformProposalDataToFormData = (setting, formData) => {
  const { id, parent } = formData
  const identity = _.get(setting, 'user.identity', {})
  const proposalPhaseType = _.get(setting, 'phase', {})
  let data = _.cloneDeep(formData)
  // 新增提案(非確認、複製提案/賣場)時預設 shipTypeId 不填寫
  // 確認、複製提案/賣場時再轉換 shipTypeId (電子票券會取得 id200+model150 要轉換成 id200150)
  if (!_.isNil(_.get(data, 'product.shipType.id'))) {
    const shipTypeId = getShipTypeIdFromAllValues(data, true)
    _.set(data, 'product.shipType.id', shipTypeId)
  }

  // 將取得時間格式 Z => +08:00 以和 flatpickr 中 minDate 及 maxDate 格式相符
  const tsPaths = [
    'listing.preOrderExpectedShipDate',
    'listing.offShelvedTs',
    'listing.onShelvedTs',
    'expiredTs'
  ]
  tsPaths.forEach(path => {
    const ts = _.get(formData, path)
    if (!_.isEmpty(ts)) {
      _.set(data, path, getDefaultTimezoneMoment(ts).format())
    }
  })

  // 如果是即期品則取代商品名稱中的即期品字樣
  if (_.get(data, 'product.isExpiringItem')) {
    _.set(
      data,
      'product.name',
      _.replace(_.get(data, 'product.name'), EXPIRING_NOTE.PATTERN, '')
    )

    // 如果是即期品也要取代進倉欄位中的即期品字樣
    const models = _.get(data, 'product.models', [])
    _.forEach(models, model => {
      const items = _.get(model, 'items', [])
      _.forEach(items, item => {
        if (item.warehouseProductName) {
          item.warehouseProductName = _.replace(item.warehouseProductName, EXPIRING_NOTE.PATTERN, '')
        }
      })
    })
  }

  // 依據SupplierProfile的設定來重設表單數值
  data = syncDataBySetting(setting, data)

  // 檢視頁時，不重設身份
  if (isNotPhaseOverview(proposalPhaseType)) {
    // 依據身份來重設資料
    data = syncDataByIdentity(setting, data)
  }

  // 商品特色欄位
  // 若編輯或複製既有提案單，既有提案單資訊為條列示，則將既有內容依序放入，多餘之內容則放入最後一條內
  const shortDescriptionField = 'product.shortDescription'
  let shortDescriptions = _.get(data, shortDescriptionField) || []
  const shortDescriptionsLength = shortDescriptions.length
  if (SHORT_DESCRIPTION_LENGTH > shortDescriptionsLength) {
    shortDescriptions = shortDescriptions.concat(
      _.times(SHORT_DESCRIPTION_LENGTH - shortDescriptionsLength, _.stubString)
    )
  }
  const finalShortDescriptions = _.flow(
    shortDescriptions => _.chunk(shortDescriptions, 4),
    shortDescriptionChunks => [
      ..._.get(shortDescriptionChunks, 0, []),
      _.reduce(
        _.get(shortDescriptionChunks, 1, []),
        (acc, text, idx) => {
          acc += text
          return acc
        },
        ''
      )
    ]
  )(shortDescriptions)
  _.set(data, shortDescriptionField, finalShortDescriptions)

  // 組成運送方式區塊
  data = setShipTypeFormSectionByFormData(setting, data)

  // 組成商品規格與屬性區塊
  data = setActiveTransientData(setting, data)

  /** make sure html content in copy has been checked */
  const copy = _.get(data, 'product.copy', '')
  const copywithHTMLCheck = withHTMLCheck(copy)
  _.set(data, 'product.copy', copywithHTMLCheck)

  // 取得商品影片
  const videoField = 'product.models[0].videos'
  let videos = _.get(data, videoField, [])
  videos = _.map(videos, video => formatUploadedVideoFile(video))

  // APACSMDESK-32303 [P3][proposal][newListing][compose] 已存過的單，[提案人]若為空，則應顯示空值，不需自動帶小幫手名稱
  // 如果尚為提案過，設定提案人預設值
  if (!id) {
    _.set(data, 'applicant', identity.name)
  }

  // APACSMDESK-33975 [P2][proposal][newListing/newProduct] 已有id的提案單其[保固期限]為"無保固"，編輯[保固期限]不為"無保固"時，[保固來源]與[保固範圍]異常
  // 清除product.warranty.period為無保固時，API會把scope及handler也寫入無保固字串，應該刪除

  const warranty = _.get(data, 'product.warranty') || {}
  if (isNoneWarrantyPeriod(warranty.period)) {
    if (isNoneWarrantyPeriod(warranty.handler)) {
      warranty.handler = null
    }
    if (isNoneWarrantyPeriod(warranty.scope)) {
      warranty.scope = null
    }
    _.set(data, 'product.warranty', warranty)
  }
  // 如果不是新品瑕疵或者產品故障則就是其他 需要特別設定其他保固範圍的值
  const warrantyScope = warranty.scope
  if (
    warrantyScope &&
    !_.includes(PRODUCT_WARRANTY_SCOPE_LIST, warrantyScope)
  ) {
    _.set(data, WARRANTY_SCOPE_FIELD, WARRANTY_SCOPE_OTHER)
    _.set(data, WARRANTY_CUSTOM_SCOPE_FIELD, warrantyScope)
  }

  const { productStatus } = warranty
  if (_.isUndefined(productStatus)) {
    _.set(data, WARRANTY_STATUS_FIELD, PRODUCT_WARRANTY_STATUS.NEW)
  }

  // 產品狀態跟預設允許的不同時強制全部轉換成 null 由表單驗證阻擋讓 user 重選
  if (!(productStatus in PRODUCT_WARRANTY_STATUS_MAP)) {
    _.set(data, WARRANTY_STATUS_FIELD, null)
  }

  // 以DEFAULT_FORM_DATA的Key來取得必要欄位，清除不需要欄位
  const formDataKeys = _.keys(DEFAULT_FORM_DATA)
  data = _.pick(data, formDataKeys)

  // 母提案資訊[parent] 只用來顯示在檢視頁 https://staging-tw.supplier.yahoo.com/docs/api/resources/proposal/#parentproposal-%e6%af%8d%e6%8f%90%e6%a1%88%e8%b3%87%e8%a8%8a
  // DEFAULT_FORM_DATA 會用來指定 post / put proposal 預設欄位，無欄位parent 須重新set parent 至redux from data
  // 若此提案單不為「修改賣場資訊提案單」(`UpdateListing`) 之子提案，則此欄位parent為空
  if (!_.isEmpty(parent)) {
    _.set(data, 'parent', parent)
  }

  data.product = {
    ..._.pick(data.product, _.keys(DEFAULT_FORM_DATA.product)),
    isWarrantyEmpty: _.get(data, 'product.isWarrantyEmpty')
  }
  data = _.omit(data, 'product.models')
  _.set(data, 'product.models', [{ videos }])

  // 應稅轉成免税欄位
  const taxable = _.get(data, 'product.taxable')
  _.set(data, 'product.taxFree', !taxable)

  // 刪除不需要欄位
  const omitList = [
    'contactWindow',
    'modifiedTimes',
    'version',
    'seqNo',
    'listing.zoneId',
    'listing.zoneName',
    'listing.catId',
    'listing.catName',
    'listing.catItemId',
    'listing.catItemName',
    'listing.subStationId',
    'listing.subStationName'
  ]
  data = _.omit(data, omitList)

  // 遊戲軟體區塊開關
  _.set(
    setting,
    'showGameContent',
    !_.isEmpty(_.get(data, 'product.gameContents', []))
  )

  // 取得運送方式區塊預設值並複寫
  const shipTypeFormData = getShipTypeDefaultFormData()

  // 根據 fetchStruDataAttrClusterOptions 取得的欄位格式 constraints 比對實際規格數值的格式
  // 如果規格表 type 是 text 但實際上取得為 array 需要幫他轉換回 text
  // ex: ['本產品已投責任險5', '000萬元整'] => ['本產品已投責任險5,000萬元整']
  const { product = {}, subStationId } = data
  const { catItemId, struDataAttrClusterId } = product
  const transientKey = getAttrTransientKey(subStationId, catItemId, struDataAttrClusterId)
  const transient = _.get(data, transientKey, {})
  const specAttributeFieldPrefix = getSpecAttributeFieldPrefixByLevel(transient.hasSpecLevel)
  const convertedAttributes = _.map(
    // 找出 getSpecificListing 取得目前設定的規格表資料
    _.get(transient, specAttributeFieldPrefix, []),
    // 按照規格名稱對應欄位應有的格式，如果格式應為 text 但資料卻是 array 就用逗號組合起來
    // 這個是兼容舊系統修改過後，規格表的值會被轉換成陣列導致送給 api 時會得到屬性或規格過多的錯誤訊息
    attr => {
      const { values = [], constraints } = attr
      const attrType = _.get(constraints, 'type')
      if (attrType === 'text' && _.isArray(values) && (_.size(values) > 1)) {
        return { ...attr, values: [_.join(values, ',')] }
      }
      return attr
    }
  )
  _.set(data, `${transientKey}.${specAttributeFieldPrefix}`, convertedAttributes)
  return _.merge({}, shipTypeFormData, data, { setting })
}

const specItemFinder = (item, level) => ({
  level,
  name: item.spec.name
})

export const specConverter = models => {
  if (!models) {
    return null
  }
  const resultSpec = [specItemFinder(models[0], 1)]

  if (models[0].items) {
    resultSpec.push(specItemFinder(models[0].items[0], 2))
  }
  return resultSpec
}

/**
 * videos在提案時不能包含複製來的duration, thumbnail等欄位
 */
export const videoConverter = videos =>
  videos && videos.map(
    video => _.pick(video, ['order', 'url']))

export const modelItemConverter = item => ({
  displayName: item.spec.selectedValue,
  spec: {
    name: item.spec.name,
    values: [item.spec.selectedValue]
  },
  barcode: item.actualBarcode,
  id: item.sku,
  isVisible: true,
  stock: item.stock,
  warehouseProductName: item.name,
  partNo: item.partNo
})

export const modelsConvert = (
  { models, images, videos, ...restListing },
  fetchedProducts) => {
  // 無屬性賣場 query listing 沒有 models
  if (!models) {
    return [{
      images,
      videos: videoConverter(videos),
      isVisible: true,
      items: [{
        isVisible: true,
        stock: restListing.stock,
        // warehouseBarcode: '一律系統產生',
        warehouseProductName: restListing.name
      }]
    }]
  } else {
    const level2SpecValues = []
    const convertedModels = models.map(model => {
      const convertedModel = ({
        displayName: model.spec.selectedValue,
        images: model.images,
        videos: videoConverter(model.videos),
        isVisible: true,
        items: model.items
          // 雙層屬性
          ? model.items.map(modelItemConverter)
          // 單層屬性
          : [_.omit(modelItemConverter(model), ['displayName', 'spec'])],
        spec: {
          name: model.spec.name,
          values: [model.spec.selectedValue]
        }
      })

      // collect level 2 specs for checking items are sufficient
      model.items && model.items.forEach(item => {
        const selectedValue = item.spec.selectedValue
        if (!level2SpecValues.includes(selectedValue)) {
          level2SpecValues.push(selectedValue)
        }
      })

      return convertedModel
    })

    /**
     * 複製賣場時，由於第二層屬性可能被編輯而缺少，將無法正常提案
     * 在此檢查第二層屬性是否有缺，並以空資料補齊 APACSMDESK-62156
     */
    if (level2SpecValues.length > 0) {
      convertedModels.map(({ items }) => {
        const convertedItems = items
        // check if level 2 items are sufficient
        if (items.length < level2SpecValues.length) {
          level2SpecValues.forEach(val => {
            if (!convertedItems.find(({ spec }) => spec.values[0] === val)) {
              // add the missing item
              convertedItems.push({
                displayName: val,
                isVisible: true,
                spec: {
                  name: convertedItems[0].spec.name,
                  values: [val]
                }
              })
            }
          })
        }
        return convertedItems
      })
    }

    return convertProductsAttrToModels(convertedModels, fetchedProducts)
  }
}

/**
 * APACSMDESK-62215 [複製賣場]有屬性賣場的實商品規格表用來判斷
 * 若與listing models內屬性規格名稱不同時，則用規格表的來複寫spec.values
 */
export const convertProductsAttrToModels = (convertedModels, fetchedProducts) => {
  const clonedModels = _.cloneDeep(convertedModels)
  if (fetchedProducts.products.length > 1) {
    clonedModels.forEach(({ items, displayName, spec }) => {
      const { name: specName, values: specValues } = spec
      items.forEach(item => {
        const fetchedProduct = fetchedProducts.products.find(
          product => product.id === item.id) || {}
        // 雙層屬性或單層屬性, 選擇最後一層spec判斷
        const compareDisplayName = item.spec ? item.displayName : displayName
        const compareSpecName = item.spec ? item.spec.name : specName
        const comparespecValues = item.spec ? item.spec.values : specValues
        const matchedAttr = _.get(fetchedProduct, 'structuredData.attributes', [])
          .find(attribute => attribute.name === compareSpecName)

        // 若displayName與specValues相同, 且從對應規格表中找得到不同的values時, 取用規格表值來複寫
        if (
          matchedAttr &&
          compareDisplayName === comparespecValues[0] &&
          compareDisplayName !== matchedAttr.values[0]) {
          if (item.spec) {
            item.spec.values = matchedAttr.values
          } else {
            spec.values = matchedAttr.values
          }
        }
      })
    })
  }
  return clonedModels
}

/**
 * 由賣場名稱判斷是不是即期品, 即名稱必須為「(即期品)」開頭
 * @param {String} name 賣場名稱
 * @returns {Bool}
 */
export const isNameRepresentExpiringItem = name =>
  name && name.startsWith('(即期品)')
